import { useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import { get, useFormContext, useWatch } from 'react-hook-form';
import FormField from 'components/inputs/FormField/FormField';
import LocationMap from 'components/common/LocationMap/LocationMap';
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect';
import CustomFormMultiSelect from 'components/inputs/CustomFormMultiSelect/CustomFormMultiSelect';
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput';
import { EFormFieldType } from 'enum/EFormFieldType';
import { useCities, useGovernorates } from 'hooks/useLookups';
import { toFieldError } from 'common/utils';

export const WEEK_DAYS = ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export interface StoreNamesFormValues {
  en: string;
  ar: string;
}

export function StoreNamesForm() {
  return (
    <>
      <FormField field={{ name: 'en', type: EFormFieldType.TEXT, label: 'English Name', placeholder: 'English name', required: true, visible: true }} />
      <FormField field={{ name: 'ar', type: EFormFieldType.TEXT, label: 'Arabic Name', placeholder: 'Arabic name', required: true, visible: true }} />
    </>
  );
}

export interface StoreAddressFormValues {
  governorateId: string;
  cityId: string;
  fullAddressEN: string;
  fullAddressAR: string;
  latitude: string;
  longitude: string;
}

// The cities offered are the picked governorate's, so a new governorate clears
// a city that belonged to the old one.
export function StoreAddressForm() {
  const { setValue, control } = useFormContext<StoreAddressFormValues>();
  const governorateId = useWatch({ control, name: 'governorateId' });
  const [latitude, longitude] = useWatch({ control, name: ['latitude', 'longitude'] });
  const previous = useRef(governorateId);

  const { data: governorates = [] } = useGovernorates();
  const { data: cities = [] } = useCities(governorateId || undefined);

  useEffect(() => {
    if (previous.current && previous.current !== governorateId) {
      setValue('cityId', '');
    }
    previous.current = governorateId;
  }, [governorateId, setValue]);

  const toOptions = (items: any[]) => items.map((item) => ({ label: item.label, value: item.value }));

  return (
    <>
      <Grid size={{ xs: 12, md: 6 }}>
        <CustomFormSelect
          name="governorateId"
          label="Governorate/Region"
          placeholder="Governorate/Region"
          required
          options={toOptions(governorates)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <CustomFormSelect
          name="cityId"
          label="City"
          placeholder="City"
          required
          options={toOptions(cities)}
          disabled={!governorateId}
        />
      </Grid>
      <FormField field={{ name: 'fullAddressEN', type: EFormFieldType.TEXT, label: 'Address (English)', placeholder: 'Address in English', required: false, visible: true }} />
      <FormField field={{ name: 'fullAddressAR', type: EFormFieldType.TEXT, label: 'Address (Arabic)', placeholder: 'Address in Arabic', required: true, visible: true }} />
      <FormField field={{ name: 'latitude', type: EFormFieldType.NUMBER, label: 'Latitude', placeholder: 'Latitude', required: false, visible: true }} />
      <FormField field={{ name: 'longitude', type: EFormFieldType.NUMBER, label: 'Longitude', placeholder: 'Longitude', required: false, visible: true }} />
      {/* Drag the pin, or click where the store is, to set the two fields above. */}
      <Grid size={12}>
        <LocationMap
          draggable
          latitude={latitude === '' ? null : Number(latitude)}
          longitude={longitude === '' ? null : Number(longitude)}
          onChange={(lat, lng) => {
            setValue('latitude', String(lat), { shouldDirty: true });
            setValue('longitude', String(lng), { shouldDirty: true });
          }}
        />
      </Grid>
    </>
  );
}

export interface StoreHoursFormValues {
  workingDays: string[];
  openingTime: string;
  closingTime: string;
}

export function StoreHoursForm() {
  const { control, register, formState: { errors } } = useFormContext<StoreHoursFormValues>();

  return (
    <>
      <Grid size={12}>
        <CustomFormMultiSelect
          name="workingDays"
          control={control}
          label="Working Days"
          placeholder="Working days"
          required
          options={WEEK_DAYS.map((day) => ({ label: capitalize(day), value: day }))}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <CustomFormTextInput
          name="openingTime"
          label="Opening Time"
          required
          type="time"
          register={register}
          error={toFieldError(get(errors, 'openingTime'))}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <CustomFormTextInput
          name="closingTime"
          label="Closing Time"
          required
          type="time"
          register={register}
          error={toFieldError(get(errors, 'closingTime'))}
        />
      </Grid>
    </>
  );
}
